// API & MCP access (INTEGRATION-04 API keys, AI-07 MCP server; owner, 2026-10-29; D-090). One list
// for the admin's key form, the server's checks and the MCP tool catalogue, so the three never drift.

/** Permissions an API key or a connected AI app can be given (the public API's routes). */
export const API_KEY_SCOPES = [
  "orders.read",
  "orders.update",
  "products.read",
  "products.write",
  "customers.read",
  "inventory.read",
  "inventory.adjust",
  "webhooks.manage",
] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

export const isApiKeyScope = (s: string): s is ApiKeyScope =>
  (API_KEY_SCOPES as readonly string[]).includes(s);

/** Ready-made permission sets; "custom" is any other combination. */
export const KEY_PRESETS = [
  {
    id: "read_only",
    scopes: ["orders.read", "products.read", "customers.read", "inventory.read"],
  },
  {
    id: "orders",
    scopes: ["orders.read", "orders.update", "customers.read", "products.read", "inventory.read"],
  },
  {
    id: "catalog",
    scopes: ["products.read", "products.write", "inventory.read", "inventory.adjust"],
  },
  { id: "full", scopes: [...API_KEY_SCOPES] },
] as const satisfies readonly { id: string; scopes: readonly ApiKeyScope[] }[];
export type KeyPresetId = (typeof KEY_PRESETS)[number]["id"] | "custom";

/** The preset whose scopes are exactly these, else "custom". */
export function presetFor(scopes: readonly string[]): KeyPresetId {
  const set = new Set(scopes);
  const hit = KEY_PRESETS.find(
    (p) => p.scopes.length === set.size && p.scopes.every((s) => set.has(s)),
  );
  return hit?.id ?? "custom";
}

/** A key's own request limit per minute: at least this, at most the platform's 120 (SECURITY §10). */
export const KEY_RATE_LIMIT_MIN = 10;
export const KEY_RATE_LIMIT_MAX = 120;

/** MCP tools (AI-07). Writes always preview first and run only with the confirm token (AI-06). */
export const MCP_TOOLS = [
  { name: "list_stores", scope: null, write: false },
  { name: "search_orders", scope: "orders.read", write: false },
  { name: "get_order", scope: "orders.read", write: false },
  { name: "search_products", scope: "products.read", write: false },
  { name: "get_product", scope: "products.read", write: false },
  { name: "list_categories", scope: "products.read", write: false },
  { name: "search_customers", scope: "customers.read", write: false },
  { name: "get_customer", scope: "customers.read", write: false },
  { name: "check_stock", scope: "inventory.read", write: false },
  { name: "update_order_status", scope: "orders.update", write: true },
  { name: "update_order_details", scope: "orders.update", write: true },
  { name: "create_product", scope: "products.write", write: true },
  { name: "update_product", scope: "products.write", write: true },
  { name: "update_variant", scope: "products.write", write: true },
  { name: "adjust_stock", scope: "inventory.adjust", write: true },
] as const satisfies readonly { name: string; scope: ApiKeyScope | null; write: boolean }[];
export type McpToolName = (typeof MCP_TOOLS)[number]["name"];

/** The MCP endpoint path on the API host (the admin shows `<origin>/mcp`). */
export const MCP_PATH = "/mcp";
